import React from 'react';
import { BottomSheet, Button } from '@parsina/framework';
import { Camera, ImageIcon, Trash2 } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// Changing the profile picture, from the device that has a camera (finding 14).
//
// Avatar capture was desktop-only: the phone showed the picture and offered no
// way to change it, which is backwards — the camera is the thing the phone HAS
// and the desktop does not. A doctor wanting their own photo on their own
// profile had to find a laptop.
//
// `capture="environment"` on a file input is the whole mechanism: iOS and
// Android both read it as «take a photo», and a browser without a camera falls
// back to the picker on its own. Two inputs rather than one, because a single
// input with `capture` set NEVER offers the gallery on iOS, and «انتخاب از
// گالری» is the more common of the two.
//
// The crop step is the SAME `AvatarCropModal` the desktop uses — the picture
// that reaches the record must be framed the same way whichever surface sent
// it, and a phone-only crop would be a second opinion about that.
// ─────────────────────────────────────────────────────────────────────────────

const Row = ({ Icon, label, onClick, testId, destructive }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    data-testid={testId}
    className="h-auto min-h-11 w-full justify-start gap-3 rounded-lg px-3 py-3"
    style={{ color: destructive ? C.red : C.textPrimary }}
  >
    <Icon size={18} aria-hidden style={{ flexShrink: 0 }} />
    <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{label}</span>
  </Button>
);

const MobileAvatarSheet = ({ open, onClose, onPick, onRemove, hasAvatar }) => {
  const { t } = useTranslation();
  const cameraRef = React.useRef(null);
  const galleryRef = React.useRef(null);

  const handle = (e) => {
    const file = e.target.files?.[0];
    // Reset first: picking the SAME file twice must fire `change` again, and it
    // will not while the input still holds it.
    e.target.value = '';
    if (file) onPick(file);
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('settings.avatar.change')}
      closeLabel={t('common.actions.close')}
      data-testid="settings-avatar-sheet"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Row
          Icon={Camera}
          label={t('settings.avatar.takePhoto')}
          onClick={() => cameraRef.current?.click()}
          testId="settings-avatar-camera"
        />
        <Row
          Icon={ImageIcon}
          label={t('settings.avatar.chooseFromGallery')}
          onClick={() => galleryRef.current?.click()}
          testId="settings-avatar-gallery"
        />
        {hasAvatar && (
          <Row
            Icon={Trash2}
            label={t('settings.avatar.remove')}
            onClick={onRemove}
            testId="settings-avatar-remove"
            destructive
          />
        )}
      </div>

      {/* Two inputs: an input carrying `capture` never offers the gallery on
          iOS, and the gallery is the more common of the two. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        onChange={handle}
        style={{ display: 'none' }}
        data-testid="settings-avatar-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handle}
        style={{ display: 'none' }}
        data-testid="settings-avatar-gallery-input"
      />
    </BottomSheet>
  );
};

export default MobileAvatarSheet;
